import { Loader, Switch } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import {
    IconCircleDot,
    IconExclamationCircle,
    IconLogout,
    IconPlayerPlay,
    IconPlayerStop,
    IconTerminal2,
    IconTrash,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import { Button } from '@xver/components/Atoms';
import type { TerminalStatus } from '@xver/hooks/CloudComputeHooks/useInstanceTerminal';
import useInstanceTerminal from '@xver/hooks/CloudComputeHooks/useInstanceTerminal';
import usePageHeading from '@xver/hooks/usePageHeading';
import { getErrorMessage } from '@xver/utils/error';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router';
import { useInstanceQuery } from '../CloudComputeInstancePage/hooks/useInstanceQuery';

const STATUS_COLORS: Record<TerminalStatus, string> = {
    idle: 'var(--color-status-neutral)',
    connecting: 'var(--color-status-warning)',
    open: 'var(--color-status-success)',
    closed: 'var(--color-status-neutral)',
    error: 'var(--color-status-danger)',
};

export default function CloudComputeTerminalPage() {
    const { t } = useTranslation('cloudComputeInstance');
    const { instanceId } = useParams<'instanceId'>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const { data: instanceData, isPending: isInstancePending } = useInstanceQuery(
        Number(instanceId)
    );

    const hostname = instanceData?.hostname ?? '';
    // Several of these tabs can be open at once, one per machine, and they are
    // indistinguishable in the tab strip without the hostname first.
    const headingRef = usePageHeading(
        ...(hostname
            ? [hostname, t('terminal.title')]
            : isInstancePending
              ? []
              : [t('terminal.title')])
    );
    const isRunning = instanceData?.state?.power === 'running';

    // The serial console is a Linux getty. Windows exposes nothing on ttyS0 without
    // extra SAC setup, so it keeps the VNC console instead.
    const isWindows = (instanceData?.image?.os ?? '').toLowerCase().includes('windows');

    const [screenReaderMode, setScreenReaderMode] = useState(false);

    const terminalStatusQuery = useQuery({
        queryKey: ['api.v1.instances.terminal', instanceId],
        queryFn: () => Endpoints.v1.Instances.getTerminalStatus(instanceId!),
        enabled: !!instanceData && !isWindows,
        staleTime: 30_000,
    });

    const serialConsoleEnabled = terminalStatusQuery.data?.enabled ?? false;
    const canConnect = serialConsoleEnabled && isRunning && !isWindows;

    const { status, error, attachContainer, connect, disconnect, clear, fit } = useInstanceTerminal(
        {
            instanceId: instanceId ?? '',
            autoConnect: canConnect,
            screenReaderMode,
        }
    );

    const { ref: sizeRef, width, height } = useElementSize();

    const setHostRef = useCallback(
        (node: HTMLDivElement | null) => {
            sizeRef(node);
            attachContainer(node);
        },
        [sizeRef, attachContainer]
    );

    useEffect(() => {
        if (width > 0 && height > 0) fit();
    }, [width, height, fit]);

    const enableMutation = useMutation({
        mutationFn: () => Endpoints.v1.Instances.enableTerminal(instanceId!),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['api.v1.instances.terminal', instanceId] });
            queryClient.invalidateQueries({
                queryKey: ['api.v1.instances.get', Number(instanceId)],
            });
        },
    });

    // Opened from the instance page in a new tab, so closing is the way back. A tab
    // the user opened themselves cannot be closed by script, so fall back to routing.
    const handleExit = useCallback(() => {
        if (window.opener) {
            window.close();
            return;
        }
        navigate(`/cloud-compute/${instanceId}`);
    }, [navigate, instanceId]);

    // The terminal box is black and empty until the first byte arrives, which is
    // indistinguishable from a broken console. Cover it while we are still
    // resolving the console state or opening the socket.
    const isPreparing = terminalStatusQuery.isPending || status === 'connecting';

    const statusColor = STATUS_COLORS[status] ?? STATUS_COLORS.idle;
    const statusLabel = t(`terminal.status.${status}`);

    if (!(Number(instanceId) > 0)) {
        return <Navigate to="/cloud-compute" replace />;
    }

    if (isInstancePending) {
        return (
            <div className="h-dvh w-full flex items-center justify-center bg-page" role="status">
                <Loader size="sm" type="bars" color="var(--fg)" />
            </div>
        );
    }

    if (isWindows) {
        return <Navigate to={`/cloud-compute/${instanceId}`} replace />;
    }

    return (
        <div className="h-dvh w-full flex flex-col bg-page text-fg">
            <header className="shrink-0 w-full px-4 py-2.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b-[0.5px] border-line">
                <div className="flex items-center gap-2 min-w-0">
                    <IconTerminal2 size={18} aria-hidden="true" />
                    <h1
                        ref={headingRef}
                        tabIndex={-1}
                        className="text-sm font-medium whitespace-nowrap"
                    >
                        {t('terminal.title')}
                    </h1>
                    <span className="text-xs font-mono text-fg/68 truncate">{hostname}</span>
                </div>

                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5" role="status">
                            <IconCircleDot
                                size={12}
                                style={{ color: statusColor }}
                                aria-hidden="true"
                            />
                            <span className="text-xs font-medium" style={{ color: statusColor }}>
                                {statusLabel}
                            </span>
                        </div>
                        <Switch
                            size="xs"
                            checked={screenReaderMode}
                            onChange={(event) => setScreenReaderMode(event.currentTarget.checked)}
                            label={t('terminal.screenReaderMode')}
                            classNames={{ label: 'whitespace-nowrap' }}
                        />
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        {status === 'open' ? (
                            <Button
                                size="sm"
                                variant="secondary"
                                leftIcon={<IconPlayerStop size={14} />}
                                onClick={disconnect}
                            >
                                {t('terminal.disconnect')}
                            </Button>
                        ) : (
                            <Button
                                size="sm"
                                leftIcon={<IconPlayerPlay size={14} />}
                                onClick={connect}
                                disabled={!canConnect}
                            >
                                {t('terminal.connect')}
                            </Button>
                        )}
                        <Button
                            size="sm"
                            variant="secondary"
                            leftIcon={<IconTrash size={14} />}
                            onClick={clear}
                        >
                            {t('terminal.clear')}
                        </Button>
                        <Button
                            size="sm"
                            variant="secondary"
                            leftIcon={<IconLogout size={14} />}
                            onClick={handleExit}
                        >
                            {t('terminal.exit')}
                        </Button>
                    </div>
                </div>
            </header>

            {error && (
                <div className="shrink-0 w-full px-4 py-2 flex items-center gap-2 bg-red-950/40 light:bg-red-50 border-b-[0.5px] border-line text-red-300 light:text-red-700">
                    <IconExclamationCircle size={16} aria-hidden="true" />
                    <span className="text-xs font-mono">{error}</span>
                </div>
            )}

            {!terminalStatusQuery.isPending && !serialConsoleEnabled && (
                <div className="shrink-0 w-full px-4 py-6 flex flex-col items-start gap-3 border-b-[0.5px] border-line">
                    <div className="flex items-center gap-2 text-fg/80">
                        <IconTerminal2 size={18} aria-hidden="true" />
                        <span className="text-sm font-medium">{t('terminal.setup.title')}</span>
                    </div>
                    <p className="text-xs text-fg/68 max-w-xl">{t('terminal.setup.description')}</p>
                    {enableMutation.isError && (
                        <span className="text-xs text-red-300 light:text-red-700 font-mono">
                            {getErrorMessage(enableMutation.error)}
                        </span>
                    )}
                    <Button
                        size="sm"
                        onClick={() => enableMutation.mutate()}
                        disabled={enableMutation.isPending}
                    >
                        {enableMutation.isPending
                            ? t('terminal.setup.enabling')
                            : t('terminal.setup.enable')}
                    </Button>
                </div>
            )}

            {serialConsoleEnabled && !isRunning && (
                <div className="shrink-0 w-full px-4 py-2 border-b-[0.5px] border-line text-xs text-fg/68">
                    {t('terminal.notRunning')}
                </div>
            )}

            <div className="relative flex-1 min-h-0">
                <div
                    ref={setHostRef}
                    className="w-full h-full p-2 bg-black overflow-hidden"
                    aria-label={t('terminal.ariaLabel', { hostname })}
                    aria-busy={isPreparing}
                />
                {isPreparing && (
                    <div
                        className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/75"
                        role="status"
                    >
                        <Loader size="sm" type="bars" color="var(--fg)" />
                        <span className="text-xs text-fg/70">{t('terminal.preparing')}</span>
                    </div>
                )}
            </div>

            <footer className="shrink-0 w-full px-4 py-2 flex justify-between items-center gap-4 border-t-[0.5px] border-line text-xs text-fg/68">
                <span>{t('terminal.exitHint')}</span>
                <span className="font-mono truncate">{hostname}</span>
            </footer>
        </div>
    );
}
